import { useEffect, useMemo, useRef, useState } from 'react'
import QueryString from 'qs'
import { capitalize } from 'lodash'
import { useRouter } from 'next/router'
import {
  HotelDestinationSchema,
  HotelResultsFormState,
  HotelResultsFormStateSchema,
} from '@reward-platform/ancillaries-schemas/hotel'
import { Coordinates } from '@reward-platform/ancillaries-schemas/common'
import { HotelSearchBrowserParams, HotelSearchBrowserParamsSchema } from '~/schemas/browserUrl'
import { useAncillariesLocationsSearch } from '~/hooks/useAncillariesLocations'
import {
  sanitizeBrowserIdentifier,
  sanitizeBrowserText,
} from '~/utils/security/sanitizeBrowserInput'
import {
  DEFAULT_DESTINATION,
  getDefaultHotelResultsFormState,
  parseRoomsFromQueryString,
  stringifyRoomsFromData,
} from './parseHotelSearchUrl'
import { preserveDateWhenConvertedToUTC } from '../../../utils/datetime'
import { consumeHotelDestinationHandoff } from './hotelDestinationHandoff'

type DestinationSignature = Pick<HotelResultsFormState['destination'], 'id' | 'type'>
type HotelResultsQueryState = HotelResultsFormState & Record<string, unknown>
const HOTEL_FORM_QUERY_KEYS = [
  'destination',
  'rooms',
  'searchString',
  'startDateTime',
  'endDateTime',
]

const hasCoordinates = (coordinates?: Coordinates) =>
  Boolean(coordinates?.latitude && coordinates.longitude)

const getDestinationSignature = (destination: HotelResultsFormState['destination']) => ({
  id: destination.id,
  type: destination.type,
})

const isDestinationSignatureChanged = ({
  previous,
  current,
}: {
  previous: DestinationSignature | undefined
  current: DestinationSignature
}) => Boolean(previous && (previous.id !== current.id || previous.type !== current.type))

const getResolvedLocation = ({
  coordinates,
  data,
  destination,
}: {
  coordinates?: Coordinates
  data: ReturnType<typeof useAncillariesLocationsSearch>['data']
  destination: HotelResultsFormState['destination']
}) => {
  if (coordinates && data && data.length > 0) {
    return data[0]
  }

  return data?.find((value) => value.id === destination.id && value.type === destination.type)
}

const resolveDestinationForReturnState = ({
  handoffDestination,
  isError,
  resolvedLocation,
  fallbackDestination,
}: {
  handoffDestination: HotelResultsFormState['destination'] | null
  isError: boolean
  resolvedLocation: HotelResultsFormState['destination'] | undefined
  fallbackDestination: HotelResultsFormState['destination']
}) =>
  handoffDestination ?? (isError ? fallbackDestination : resolvedLocation ?? fallbackDestination)

const hasValidDestination = (destination: HotelResultsFormState['destination']): boolean =>
  HotelDestinationSchema.safeParse(destination).success

const getUnresolvedDestinationFallback = (
  destination: HotelResultsFormState['destination']
): HotelResultsFormState['destination'] =>
  hasValidDestination(destination) ? destination : DEFAULT_DESTINATION

export const stringifyHotelSearchFormToQuery = (params?: HotelResultsFormState): string => {
  if (!params) {
    return ''
  }

  const queryParams: HotelSearchBrowserParams = {
    dest_search: params.destination.fullName ?? DEFAULT_DESTINATION.name,
    dest_id: params.destination.id ?? DEFAULT_DESTINATION.id,
    dest_type: params.destination.type ?? DEFAULT_DESTINATION.type,
    startDateTime: preserveDateWhenConvertedToUTC(params.startDateTime).toISOString(),
    endDateTime: preserveDateWhenConvertedToUTC(params.endDateTime).toISOString(),
    occupancy: params.rooms.length ? stringifyRoomsFromData(params.rooms) : undefined,
  }

  return QueryString.stringify(queryParams)
}

const mergeDefinedQueryParams = (
  current: Record<string, unknown>,
  next: Record<string, unknown>
): Record<string, unknown> => ({
  ...current,
  ...Object.fromEntries(Object.entries(next).filter(([, v]) => v !== undefined)),
})

export const buildHotelResultsQuery = (
  currentQueryString: string,
  formState: HotelResultsQueryState
) => {
  const current = QueryString.parse(currentQueryString, { ignoreQueryPrefix: true })
  const formParams = QueryString.parse(stringifyHotelSearchFormToQuery(formState))
  const extraParams = Object.fromEntries(
    Object.entries(formState).filter(([key]) => !HOTEL_FORM_QUERY_KEYS.includes(key))
  )

  return QueryString.stringify(mergeDefinedQueryParams(current, { ...formParams, ...extraParams }))
}

const parseModernHotelSearchQuery = ({
  queryState,
  defaultHotelSearchParams,
}: {
  queryState: unknown
  defaultHotelSearchParams: HotelResultsFormState
}): HotelResultsFormState | null => {
  const parsedQuery = HotelSearchBrowserParamsSchema.safeParse(queryState)
  if (!parsedQuery.success) {
    return null
  }

  const occupancyString = parsedQuery.data.occupancy ?? parsedQuery.data.rooms
  const sanitizedDestinationSearch = sanitizeBrowserText(parsedQuery.data.dest_search, 160)

  return {
    searchString: sanitizedDestinationSearch,
    ...defaultHotelSearchParams,
    destination: {
      id: sanitizeBrowserIdentifier(parsedQuery.data.dest_id, 64),
      type: sanitizeBrowserIdentifier(parsedQuery.data.dest_type, 64),
      fullName: capitalize(sanitizedDestinationSearch),
      name: capitalize(sanitizedDestinationSearch),
    },
    ...(occupancyString ? { rooms: parseRoomsFromQueryString(occupancyString) } : {}),
    ...(parsedQuery.data.startDateTime && parsedQuery.data.endDateTime
      ? {
          startDateTime: preserveDateWhenConvertedToUTC(new Date(parsedQuery.data.startDateTime)),
          endDateTime: preserveDateWhenConvertedToUTC(new Date(parsedQuery.data.endDateTime)),
        }
      : {}),
  }
}

const parseLegacyHotelSearchQuery = (queryState: unknown): HotelResultsFormState | null => {
  const legacyQueryToState = HotelResultsFormStateSchema.transform((query) => ({
    destination: query.destination,
    rooms: query.rooms,
    endDateTime: preserveDateWhenConvertedToUTC(new Date(query.endDateTime)),
    startDateTime: preserveDateWhenConvertedToUTC(new Date(query.startDateTime)),
  }))

  const legacyParsedQuery = legacyQueryToState.safeParse(queryState)

  return legacyParsedQuery.success ? legacyParsedQuery.data : null
}

const parseHotelSearchQueryParamsToFormState = (queryState: unknown): HotelResultsFormState => {
  const defaultHotelSearchParams = getDefaultHotelResultsFormState()

  const modernQueryToState = parseModernHotelSearchQuery({
    queryState,
    defaultHotelSearchParams,
  })
  if (modernQueryToState) {
    return modernQueryToState
  }

  const legacyQueryToState = parseLegacyHotelSearchQuery(queryState)
  if (legacyQueryToState) {
    return legacyQueryToState
  }

  return defaultHotelSearchParams
}

const getDestinationSearchString = (searchState: HotelResultsFormState): string =>
  searchState.searchString ?? searchState.destination.fullName

const getHandoffDestination = (
  coordinates: Coordinates | undefined,
  searchState: HotelResultsFormState
): HotelResultsFormState['destination'] | null => {
  if (coordinates) {
    return null
  }

  return consumeHotelDestinationHandoff({
    destSearch: getDestinationSearchString(searchState),
    destId: searchState.destination.id,
    destType: searchState.destination.type,
  })
}

const resolveInitialHandoffDestination = ({
  coordinates,
  isHydrated,
  isReady,
  hasValidDestinationParams,
  searchState,
}: {
  coordinates?: Coordinates
  isHydrated: boolean
  isReady: boolean
  hasValidDestinationParams: boolean
  searchState: HotelResultsFormState
}): HotelResultsFormState['destination'] | null | undefined => {
  if (coordinates) {
    return null
  }
  if (!isHydrated || !isReady || !hasValidDestinationParams) {
    return undefined
  }
  return getHandoffDestination(coordinates, searchState)
}

const canEnableLocationResolver = ({
  isHydrated,
  isReady,
  hasValidDestinationParams,
  handoffDestination,
}: {
  isHydrated: boolean
  isReady: boolean
  hasValidDestinationParams: boolean
  handoffDestination: HotelResultsFormState['destination'] | null
}) => isHydrated && isReady && hasValidDestinationParams && !handoffDestination

const shouldResetHandoffDestination = ({
  currentHandoffDestination,
  destinationSignatureChanged,
  coordinates,
}: {
  currentHandoffDestination: HotelResultsFormState['destination'] | null | undefined
  destinationSignatureChanged: boolean
  coordinates?: Coordinates
}) =>
  currentHandoffDestination === undefined ||
  destinationSignatureChanged ||
  hasCoordinates(coordinates)

const useHotelDestinationResolution = ({
  parsedSearchState,
  coordinates,
  isHydrated,
  isReady,
}: {
  parsedSearchState: HotelResultsFormState
  coordinates?: Coordinates
  isHydrated: boolean
  isReady: boolean
}): {
  handoffDestination: HotelResultsFormState['destination'] | null
  isResolverEnabled: boolean
} => {
  const hasDestination =
    parsedSearchState.destination.id !== '' && parsedSearchState.destination.type !== ''

  const handoffDestinationRef = useRef<HotelResultsFormState['destination'] | null | undefined>(
    undefined
  )
  const previousDestinationSignatureRef = useRef<DestinationSignature | undefined>(undefined)
  const destinationSignature = getDestinationSignature(parsedSearchState.destination)
  const destinationSignatureChanged = isDestinationSignatureChanged({
    previous: previousDestinationSignatureRef.current,
    current: destinationSignature,
  })

  // Destination can be reset based on coordinates or when the destination in the URL changes
  if (
    shouldResetHandoffDestination({
      currentHandoffDestination: handoffDestinationRef.current,
      destinationSignatureChanged,
      coordinates,
    })
  ) {
    handoffDestinationRef.current = resolveInitialHandoffDestination({
      coordinates,
      isHydrated,
      isReady,
      hasValidDestinationParams: hasDestination,
      searchState: parsedSearchState,
    })
    previousDestinationSignatureRef.current = destinationSignature
  }

  const handoffDestination = handoffDestinationRef.current ?? null

  return {
    handoffDestination,
    isResolverEnabled: canEnableLocationResolver({
      isHydrated,
      isReady,
      hasValidDestinationParams: hasDestination,
      handoffDestination,
    }),
  }
}

export const useHotelSearchParams = (
  coordinates?: Coordinates
): {
  data: HotelResultsFormState
  isResolvingDestination: boolean
} => {
  const { query: queryState, isReady } = useRouter()
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const sanitizedQueryState = useMemo(() => {
    // sanitizing next router as doesn't handle nested queries correctly
    const sanitizedQuery = QueryString.stringify(queryState, { arrayFormat: 'comma' })
    return QueryString.parse(sanitizedQuery, { parseArrays: true })
  }, [queryState])

  const parsedSearchState = useMemo(
    () => parseHotelSearchQueryParamsToFormState(sanitizedQueryState),
    [sanitizedQueryState]
  )

  const { handoffDestination, isResolverEnabled } = useHotelDestinationResolution({
    parsedSearchState,
    coordinates,
    isHydrated,
    isReady,
  })

  const { data, isFetching, isError, isFetched, isPlaceholderData } = useAncillariesLocationsSearch(
    'HOTEL',
    {
      searchString: getDestinationSearchString(parsedSearchState),
      latitude: coordinates?.latitude,
      longitude: coordinates?.longitude,
    },
    { isEnabled: isResolverEnabled }
  )

  const resolvedLocation = useMemo(
    () =>
      isPlaceholderData
        ? undefined
        : getResolvedLocation({
            coordinates,
            data,
            destination: parsedSearchState.destination,
          }),
    [coordinates, data, isPlaceholderData, parsedSearchState.destination]
  )

  const returnData = useMemo<HotelResultsFormState>(
    () => ({
      ...parsedSearchState,
      destination: resolveDestinationForReturnState({
        handoffDestination,
        isError,
        resolvedLocation,
        fallbackDestination: getUnresolvedDestinationFallback(parsedSearchState.destination),
      }),
    }),
    [handoffDestination, isError, parsedSearchState, resolvedLocation]
  )

  const isResolvingDestination =
    hasValidDestination(parsedSearchState.destination) &&
    !handoffDestination &&
    !resolvedLocation &&
    !isError &&
    (!isResolverEnabled || isFetching || !isFetched)

  return {
    data: returnData,
    isResolvingDestination,
  }
}
